<template>
  <div
    role="search"
    :class="['search-field', { 'search-field--disabled': disabled }]"
  >
    <input
      :id="inputId"
      type="search"
      class="search-field__input"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      autocomplete="off"
      @input="onInput($event.target.value)"
      @keydown.enter="triggerSearch"
    />
    <button
      type="button"
      class="search-field__icon-btn"
      :aria-label="$t('search.searchLabel')"
      :disabled="disabled"
      @click="triggerSearch"
    >
      <span class="material-icons search-field__icon" aria-hidden="true">search</span>
    </button>
  </div>
</template>

<script setup>
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t: $t } = useI18n()

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Search...'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  minLength: {
    type: Number,
    default: 3
  }
})

const emit = defineEmits(['update:modelValue', 'search', 'reset'])

const inputId = 'search-field-' + Math.random().toString(36).slice(2, 9)

function onInput(value) {
  emit('update:modelValue', value)
  if (value === '') emit('reset')
}

function triggerSearch() {
  if (props.modelValue.length >= props.minLength) {
    emit('search')
  }
}

watch(
  () => props.modelValue,
  (val) => {
    if (val === '') emit('reset')
  }
)
</script>

<style scoped>
/* $white=#ffffff  $bluFour=#002e5f  $graySeven=#b8c4ce  $textGray=#5f6b7a
   $fontMiddle=1rem  $fontNormal=1.125rem  $spacingCellSm=0.9rem  $spacingCellLg=1.25rem */
.search-field {
  display: flex;
  align-items: center;
  background: #ffffff; /* $white */
  border-radius: 999px;
  border: 1.5px solid rgba(0, 46, 95, 0.18); /* rgba($bluFour, 0.18) — idle sottile */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  height: 56px;
  padding: 0 0.9rem 0 1.25rem; /* $spacingCellSm / $spacingCellLg */
  transition: box-shadow 0.3s ease, border-color 0.3s ease, background-color 0.3s ease;
}

.search-field:focus-within {
  border-color: rgba(0, 46, 95, 0.52); /* rgba($bluFour, 0.52) — più deciso */
  box-shadow: 0 5px 24px rgba(0, 46, 95, 0.22); /* rgba($bluFour, 0.22) */
  background-color: #ffffff; /* $white */
}

.search-field__input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  font-family: OpenSansRegular;
  font-size: 1rem; /* $fontMiddle */
  color: rgba(0, 46, 95, 0.5); /* rgba($bluFour, 0.5) — idle leggero */
  appearance: none;
  transition: color 0.3s ease;
}

.search-field:focus-within .search-field__input {
  color: #002e5f; /* $bluFour — focus deciso */
}

.search-field__input::placeholder {
  color: rgba(0, 46, 95, 0.28); /* rgba($bluFour, 0.28) — idle quasi invisibile */
  transition: color 0.3s ease;
}

.search-field:focus-within .search-field__input::placeholder {
  color: #b8c4ce; /* $graySeven — visibile in focus */
}

.search-field__input::-webkit-search-decoration,
.search-field__input::-webkit-search-cancel-button {
  appearance: none;
}

.search-field__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.search-field__icon-btn:disabled { cursor: default; }

.search-field__icon {
  font-size: 1.125rem; /* $fontNormal */
  color: rgba(0, 46, 95, 0.25); /* rgba($bluFour, 0.25) — idle quasi invisibile */
  transition: color 0.3s ease;
}

.search-field:focus-within .search-field__icon {
  color: #002e5f; /* $bluFour — focus deciso */
}

.search-field__icon-btn:not(:disabled):hover .search-field__icon {
  color: #5f6b7a; /* $textGray */
}

.search-field--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.search-field--disabled .search-field__input {
  cursor: not-allowed;
}
</style>